import type { MarketChartData } from '@/components/chart/market-chart';
import { LIVE_WINDOW_SECONDS } from '@/lib/chart/chart-factory';
import {
  buildLiveTradeCandles,
  buildLiveTradeSeries,
  LIVE_CANDLE_BUCKET_SECONDS,
  type LiveTradeSeriesRow,
} from '@/lib/chart/live-trade-series';
import type { ChartInterval, YAxisProperty } from '@/lib/chart/types';

export type HistoricalWinnerChartInterval = Exclude<ChartInterval, 'live'>;

interface WinnerLiveChartSource {
  readonly kind: 'live';
  readonly trades: ReadonlyArray<LiveTradeSeriesRow>;
  readonly nowSec: number;
  readonly candleMode: boolean;
}

interface WinnerHistoricalChartSource {
  readonly kind: 'historical';
  readonly interval: HistoricalWinnerChartInterval;
  readonly data: MarketChartData;
  readonly candleWidthSeconds: number;
  readonly rangeStart?: string;
}

export type WinnerChartSource = WinnerLiveChartSource | WinnerHistoricalChartSource;

export interface WinnerChartSnapshot {
  readonly chartData: Readonly<Record<number, MarketChartData>>;
  readonly candleWidthSeconds: number;
}

interface WinnerChartSourceContext {
  readonly originalOutcomeIndex: number;
  readonly yAxisProperty: YAxisProperty;
  readonly indexedOutcomeMintSupplyD6: string;
}

export function buildWinnerChartSnapshot(
  source: WinnerChartSource,
  context: WinnerChartSourceContext,
): WinnerChartSnapshot {
  if (source.kind === 'live') return buildLiveSnapshot(source, context);
  return {
    chartData: {
      0: source.data,
    },
    candleWidthSeconds: source.candleWidthSeconds,
  };
}

function buildLiveSnapshot(
  source: WinnerLiveChartSource,
  context: WinnerChartSourceContext,
): WinnerChartSnapshot {
  const mode = context.yAxisProperty === 'price' ? 'price' : 'marketCap';
  const indexedOutcomeMintSupplyD6ByOutcome = new Map([
    [context.originalOutcomeIndex, context.indexedOutcomeMintSupplyD6],
  ]);
  const cutoffSec = source.nowSec - LIVE_WINDOW_SECONDS;

  if (source.candleMode) {
    const ohlcv =
      buildLiveTradeCandles({
        trades: source.trades,
        cutoffSec,
        nowSec: source.nowSec,
        mode,
        restrictedOutcomeIndex: context.originalOutcomeIndex,
        indexedOutcomeMintSupplyD6ByOutcome,
      })[context.originalOutcomeIndex] ?? [];
    return {
      chartData: {
        0: { line: ohlcv.map((candle) => ({ time: candle.time, value: candle.close })), ohlcv },
      },
      candleWidthSeconds: LIVE_CANDLE_BUCKET_SECONDS,
    };
  }

  const line =
    buildLiveTradeSeries({
      trades: source.trades,
      cutoffSec,
      mode,
      restrictedOutcomeIndex: context.originalOutcomeIndex,
      liveStepLine: true,
      indexedOutcomeMintSupplyD6ByOutcome,
    })[context.originalOutcomeIndex] ?? [];
  return {
    chartData: { 0: { line, ohlcv: [] } },
    candleWidthSeconds: LIVE_CANDLE_BUCKET_SECONDS,
  };
}
